import { useState, type ReactNode, type FormEvent } from 'react';
import { useApi, apiPost, Cargando, ErrorApi, Vacio, Badge, Modal, BotonAccion, confirmar } from '../shared/ui.tsx';
import { LARGO_MINIMO_CLAVE, MedidorClave } from '../shared/MedidorClave.tsx';
import type { Cliente, Salon } from '../shared/tipos.ts';

type ClienteConSalones = Cliente & { salones: Salon[]; usuarios: { id: string; email: string; activo: number }[] };

export function ClientesPage(): ReactNode {
  const { datos, cargando, error, recargar } = useApi<(Cliente & { salones_count: number })[]>('/api/clientes');
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editar, setEditar] = useState<Cliente | null>(null);
  const [verDetalle, setVerDetalle] = useState<string | null>(null);

  return (
    <div className="space-y-3">
      <div className="flex justify-between">
        <h2 className="text-xl font-bold text-neutro-800">Clientes y salones</h2>
        <BotonAccion onClick={() => { setEditar(null); setMostrarForm(true); }}>+ Nuevo cliente</BotonAccion>
      </div>
      {cargando ? <Cargando /> : error ? <ErrorApi mensaje={error} onReintentar={recargar} /> : datos && datos.length > 0 ? (
        <div className="overflow-x-auto rounded-2xl bg-white shadow-sm border border-contorno-var">
          <table className="w-full text-sm">
            <thead className="bg-superficie-100 text-left text-neutro-800">
              <tr>
                <th className="px-4 py-3 font-semibold">Cliente</th>
                <th className="px-4 py-3 font-semibold">Contacto</th>
                <th className="px-4 py-3 font-semibold">Salones</th>
                <th className="px-4 py-3 font-semibold">Estado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {datos.map((c) => (
                <tr key={c.id} className="border-t border-contorno-var hover:bg-superficie-100/50">
                  <td className="px-4 py-3">
                    <button className="font-medium text-neutro-800 hover:underline" onClick={() => setVerDetalle(c.id)}>{c.nombre}</button>
                    {c.rut && <div className="text-xs text-neutro-400">RUT: {c.rut}</div>}
                  </td>
                  <td className="px-4 py-3">
                    {c.contacto && <div>{c.contacto}</div>}
                    {c.telefono && <div className="text-xs text-neutro-400">{c.telefono}</div>}
                  </td>
                  <td className="px-4 py-3">{c.salones_count}</td>
                  <td className="px-4 py-3">{c.activo ? <Badge color="verde">Activo</Badge> : <Badge color="gris">Inactivo</Badge>}</td>
                  <td className="px-4 py-3 text-right space-x-3 text-xs">
                    <button className="text-neutro-700 hover:underline" onClick={() => { setEditar(c); setMostrarForm(true); }}>Editar</button>
                    {c.activo === 1 && (
                      <button className="text-estado-error hover:underline" onClick={async () => {
                        if (confirmar(`¿Desactivar a "${c.nombre}"?`)) {
                          await apiPost(`/api/clientes/${c.id}`, undefined, 'DELETE');
                          recargar();
                        }
                      }}>Desactivar</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <Vacio texto="No hay clientes." />}

      {mostrarForm && (
        <FormCliente cliente={editar} onCerrar={() => setMostrarForm(false)} onGuardado={() => { setMostrarForm(false); recargar(); }} />
      )}
      {verDetalle && (
        <DetalleCliente clienteId={verDetalle} onCerrar={() => setVerDetalle(null)} onCambio={recargar} />
      )}
    </div>
  );
}

function FormCliente({ cliente, onCerrar, onGuardado }: { cliente: Cliente | null; onCerrar: () => void; onGuardado: () => void }): ReactNode {
  const [form, setForm] = useState({
    nombre: cliente?.nombre ?? '',
    rut: cliente?.rut ?? '',
    contacto: cliente?.contacto ?? '',
    telefono: cliente?.telefono ?? '',
    email: cliente?.email ?? '',
  });
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function guardar(e: FormEvent) {
    e.preventDefault();
    setErr(null); setGuardando(true);
    try {
      if (cliente) await apiPost(`/api/clientes/${cliente.id}`, form, 'PUT');
      else await apiPost('/api/clientes', form);
      onGuardado();
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Error'); }
    finally { setGuardando(false); }
  }

  return (
    <Modal abierto={true} titulo={cliente ? 'Editar cliente' : 'Nuevo cliente'} onCerrar={onCerrar}>
      <form onSubmit={guardar} className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-neutro-800">Nombre*</span>
          <input className="campo" required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">RUT</span>
            <input className="campo" value={form.rut} onChange={(e) => setForm({ ...form, rut: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Contacto</span>
            <input className="campo" value={form.contacto} onChange={(e) => setForm({ ...form, contacto: e.target.value })} />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Teléfono</span>
            <input className="campo" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Email</span>
            <input className="campo" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>
        </div>
        {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-estado-error">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <BotonAccion type="button" variante="secundario" onClick={onCerrar}>Cancelar</BotonAccion>
          <BotonAccion type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</BotonAccion>
        </div>
      </form>
    </Modal>
  );
}

function DetalleCliente({ clienteId, onCerrar, onCambio }: { clienteId: string; onCerrar: () => void; onCambio: () => void }): ReactNode {
  const { datos, cargando, error, recargar } = useApi<ClienteConSalones>(`/api/clientes/${clienteId}`);
  const [mostrarSalon, setMostrarSalon] = useState(false);
  const [editandoSalon, setEditandoSalon] = useState<string | null>(null);
  const [mostrarUsuario, setMostrarUsuario] = useState(false);

  async function guardarSalon(e: FormEvent, form: FormSalonDatos, salonId?: string) {
    e.preventDefault();
    const coords = parsearCoordenadas(form.coordenadas);
    if (form.coordenadas.trim() && !coords) {
      alert('Coordenadas inválidas. Pegá lo que copia Google Maps, por ejemplo: -34.90111, -56.16453');
      return;
    }
    const payload = {
      nombre: form.nombre,
      direccion: form.direccion,
      notas: form.notas,
      latitud: coords?.lat ?? null,
      longitud: coords?.lng ?? null,
      radio_metros: parseInt(form.radio_metros, 10) || 500,
      kiosk_pin: form.kiosk_pin || null,
    };
    if (salonId) {
      await apiPost(`/api/salones/${salonId}`, payload, 'PUT');
    } else {
      await apiPost(`/api/clientes/${clienteId}/salones`, payload);
    }
    recargar(); onCambio();
  }

  async function guardarUsuario(e: FormEvent, form: { email: string; password: string }) {
    e.preventDefault();
    await apiPost(`/api/clientes/${clienteId}/usuarios`, form);
    recargar(); onCambio();
  }

  return (
    <Modal abierto={true} titulo={datos?.nombre ?? 'Cliente'} onCerrar={onCerrar}>
      {cargando ? <Cargando /> : error ? <ErrorApi mensaje={error} onReintentar={recargar} /> : datos ? (
        <div className="space-y-4">
          {/* Salones */}
          <section>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-neutro-800">Salones ({datos.salones.length})</h3>
              <button className="text-sm text-primario-400 hover:underline" onClick={() => setMostrarSalon(!mostrarSalon)}>+ Agregar</button>
            </div>
            {mostrarSalon && <FormSalon onGuardar={(e, f) => { void guardarSalon(e, f); setMostrarSalon(false); }} onCerrar={() => setMostrarSalon(false)} />}
            <ul className="mt-2 space-y-1.5">
              {datos.salones.map((s) => (
                <li key={s.id} className="rounded-lg bg-superficie-100/50 px-3 py-2 text-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium">{s.nombre}</span>
                      {s.direccion && <span className="text-neutro-400"> · {s.direccion}</span>}
                      {s.activo === 0 && <Badge color="gris">Inactivo</Badge>}
                      {s.activo === 1 && (s.latitud == null || s.longitud == null) && (
                        <span className="ml-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">Sin ubicación</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {s.activo === 1 && (
                        <button className="text-xs text-primario-400 hover:underline" onClick={() => setEditandoSalon(editandoSalon === s.id ? null : s.id)}>
                          {editandoSalon === s.id ? 'Cerrar' : 'Editar'}
                        </button>
                      )}
                      {s.activo === 1 && s.kiosk_pin && (
                        <button
                          className="text-xs text-primario-400 hover:underline font-medium"
                          onClick={() => window.open(`/kiosk?salon_id=${s.id}&pin=${s.kiosk_pin}`, '_blank')}
                        >
                          Abrir Kiosk
                        </button>
                      )}
                      {s.activo === 1 && (
                        <button className="text-xs text-estado-error hover:underline" onClick={async () => {
                          if (confirmar(`¿Desactivar salón "${s.nombre}"?`)) {
                            await apiPost(`/api/salones/${s.id}`, undefined, 'DELETE');
                            recargar(); onCambio();
                          }
                        }}>Desactivar</button>
                      )}
                    </div>
                  </div>
                  {editandoSalon === s.id && (
                    <FormSalon
                      inicial={s}
                      onGuardar={(e, f) => { void guardarSalon(e, f, s.id); setEditandoSalon(null); }}
                      onCerrar={() => setEditandoSalon(null)}
                    />
                  )}
                </li>
              ))}
              {datos.salones.length === 0 && <li className="text-xs text-neutro-400">Sin salones.</li>}
            </ul>
          </section>

          {/* Usuarios portal */}
          <section>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-neutro-800">Accesos al portal ({datos.usuarios.length})</h3>
              <button className="text-sm text-primario-400 hover:underline" onClick={() => setMostrarUsuario(!mostrarUsuario)}>+ Crear acceso</button>
            </div>
            {mostrarUsuario && <FormUsuario onGuardar={guardarUsuario} onCerrar={() => setMostrarUsuario(false)} />}
            <ul className="mt-2 space-y-1.5">
              {datos.usuarios.map((u) => (
                <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-superficie-100/50 px-3 py-2 text-sm">
                  <span className={u.activo === 1 ? '' : 'text-neutro-400 line-through'}>{u.email}</span>
                  {u.activo === 1 ? (
                    <span className="flex items-center gap-3 text-xs">
                      <button className="font-semibold text-primario-400 hover:underline" onClick={async () => {
                        const nuevo = prompt(`Nuevo email para ${u.email}:`, u.email)?.trim().toLowerCase();
                        if (!nuevo || nuevo === u.email) return;
                        try {
                          await apiPost(`/api/usuarios/${u.id}`, { email: nuevo }, 'PUT');
                          recargar(); onCambio();
                        } catch (e) { alert(e instanceof Error ? e.message : 'Error'); }
                      }}>Editar mail</button>
                      <button className="font-semibold text-primario-400 hover:underline" onClick={async () => {
                        if (!confirmar(`¿Generar una contraseña nueva para ${u.email}? La actual deja de servir.`)) return;
                        const clave = generarClave(12);
                        try {
                          await apiPost(`/api/usuarios/${u.id}`, { password: clave }, 'PUT');
                          prompt(`Contraseña nueva de ${u.email} — copiala AHORA, no se vuelve a mostrar:`, clave);
                        } catch (e) { alert(e instanceof Error ? e.message : 'Error'); }
                      }}>Clave nueva</button>
                      <button className="text-estado-error hover:underline" onClick={async () => {
                        if (confirmar(`¿Eliminar el acceso de ${u.email}? No va a poder entrar más con ese correo.`)) {
                          await apiPost(`/api/usuarios/${u.id}`, undefined, 'DELETE');
                          recargar(); onCambio();
                        }
                      }}>Eliminar</button>
                    </span>
                  ) : <Badge color="gris">Inactivo</Badge>}
                </li>
              ))}
              {datos.usuarios.length === 0 && <li className="text-xs text-neutro-400">Sin accesos creados.</li>}
            </ul>
          </section>
        </div>
      ) : null}
    </Modal>
  );
}

type FormSalonDatos = { nombre: string; direccion: string; notas: string; coordenadas: string; radio_metros: string; kiosk_pin: string };

/** Parsea coordenadas pegadas desde Google Maps: "-34.90111, -56.16453". */
export function parsearCoordenadas(texto: string): { lat: number; lng: number } | null {
  const partes = texto.trim().split(/[,;]\s*|\s+/).filter(Boolean);
  if (partes.length !== 2) return null;
  const lat = Number(partes[0]);
  const lng = Number(partes[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

function FormSalon({ inicial, onGuardar, onCerrar }: { inicial?: Salon; onGuardar: (e: FormEvent, form: FormSalonDatos) => void; onCerrar: () => void }): ReactNode {
  const [form, setForm] = useState<FormSalonDatos>({
    nombre: inicial?.nombre ?? '',
    direccion: inicial?.direccion ?? '',
    notas: inicial?.notas ?? '',
    coordenadas: inicial?.latitud != null && inicial?.longitud != null ? `${inicial.latitud}, ${inicial.longitud}` : '',
    radio_metros: String(inicial?.radio_metros ?? 500),
    kiosk_pin: inicial?.kiosk_pin ?? '',
  });
  const [guardando, setGuardando] = useState(false);
  const coordsValidas = !form.coordenadas.trim() || parsearCoordenadas(form.coordenadas) !== null;
  return (
    <form onSubmit={(e) => { setGuardando(true); onGuardar(e, form); setGuardando(false); }} className="mt-2 space-y-2 rounded-lg bg-white p-3">
      <input className="campo" placeholder="Nombre del salón*" required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
      <input className="campo" placeholder="Dirección" value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} />
      <textarea className="campo" rows={2} placeholder="Notas (cómo entrar, referente…)" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
      <div>
        <input
          className={`campo ${!coordsValidas ? 'border-estado-error' : ''}`}
          placeholder="Coordenadas (pegar desde Google Maps)"
          value={form.coordenadas}
          onChange={(e) => setForm({ ...form, coordenadas: e.target.value })}
        />
        <p className="mt-1 text-xs text-neutro-400">
          En Google Maps: click derecho sobre el local → click en los números de arriba → pegalos acá (ej: -34.90111, -56.16453)
        </p>
        {!coordsValidas && <p className="text-xs text-estado-error">Formato inválido — tienen que ser dos números separados por coma.</p>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mb-0.5 block text-xs text-neutro-400">Radio de check-in (metros)</span>
          <input className="campo" type="number" min={20} required value={form.radio_metros} onChange={(e) => setForm({ ...form, radio_metros: e.target.value })} />
        </label>
        <label className="block">
          <span className="mb-0.5 block text-xs text-neutro-400">PIN kiosk (4-8 dígitos)</span>
          <input className="campo" type="text" inputMode="numeric" maxLength={8} value={form.kiosk_pin} onChange={(e) => setForm({ ...form, kiosk_pin: e.target.value })} />
        </label>
      </div>
      <div className="flex gap-2">
        <BotonAccion type="button" variante="secundario" onClick={onCerrar}>Cancelar</BotonAccion>
        <BotonAccion type="submit" disabled={guardando || !coordsValidas}>{inicial ? 'Guardar cambios' : 'Agregar'}</BotonAccion>
      </div>
    </form>
  );
}

function FormUsuario({ onGuardar, onCerrar }: { onGuardar: (e: FormEvent, form: { email: string; password: string }) => void; onCerrar: () => void }): ReactNode {
  const [form, setForm] = useState({ email: '', password: '' });
  const [guardando, setGuardando] = useState(false);
  return (
    <form onSubmit={(e) => { setGuardando(true); onGuardar(e, form); setGuardando(false); }} className="mt-2 space-y-2 rounded-lg bg-white p-3">
      <input className="campo" type="email" placeholder="Email*" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      <input className="campo" type="password" placeholder={`Contraseña (mín. ${LARGO_MINIMO_CLAVE})*`} required minLength={LARGO_MINIMO_CLAVE} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
      <MedidorClave password={form.password} />
      <div className="flex gap-2">
        <BotonAccion type="button" variante="secundario" onClick={onCerrar}>Cancelar</BotonAccion>
        <BotonAccion type="submit" disabled={guardando}>Crear acceso</BotonAccion>
      </div>
    </form>
  );
}
/** Contraseña al azar alfanumérica sin caracteres ambiguos (0/O, 1/l/I), para resets desde el admin. */
function generarClave(largo: number): string {
  const alfabeto = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint8Array(largo);
  crypto.getRandomValues(bytes);
  return [...bytes].map((b) => alfabeto[b % alfabeto.length]).join('');
}
